import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useFormValidation } from '../hooks/useFormValidation';
import { authService } from '../services/authService';
import { useApp } from '../context/AppContext';
import { getLoginRedirectRoute } from '../utils/navigation';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
  email: string;
  onBack: () => void;
  onSuccess: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  email,
  onBack,
  onSuccess,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { errors, clearError, hasErrors } = useFormValidation();
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);
    setApiError(null);
    
    if (value.trim()) {
      clearError('password');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (hasErrors || !password.trim()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      const response = await authService.login(email, password);
      
      // Store token and user data
      authService.setToken(response.data.token);
      dispatch({
        type: 'SET_USER',
        payload: {
          id: response.data.user.id,
          email: response.data.user.email,
          displayName: `${response.data.user.firstName} ${response.data.user.lastName}`,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          role: response.data.user.role,
        },
      });
      
      // Navigate to appropriate dashboard based on role
      const redirectRoute = getLoginRedirectRoute(response.data.user, state.redirectUrl);
      navigate(redirectRoute);
      
      onSuccess();
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Handle form submission
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSubmit(event);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            component="img"
            src="/logos/logo-wide.png"
            alt="Careva"
            sx={{
              height: 60,
              width: 'auto',
              maxWidth: '100%',
              mb: 2,
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1">
              Welcome Back
            </Typography>
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Sign in to your account
        </Typography>

        <form 
          ref={formRef}
          onSubmit={handleFormSubmit} 
          method="post" 
          noValidate 
          autoComplete="on"
        >
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            name="email"
            autoComplete="email"
            sx={{ mb: 3 }}
            helperText="Email address (cannot be changed)"
            InputProps={{
              readOnly: true,
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={!!errors.password}
            helperText={errors.password}
            disabled={isLoading}
            sx={{ mb: 3 }}
            name="password"
            autoComplete="current-password"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading || hasErrors || !password.trim()}
            sx={{ mb: 2 }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            Don't have an account?{' '}
            <Typography
              component="span"
              variant="body2"
              color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={onBack}
            >
              Go back to create one
            </Typography>
          </Typography>
        </form>
      </Paper>
    </Box>
  );
};
